import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { waLink, LOGO_NAVY, LOGO_LIGHT } from "@/lib/content";

const links = [
  { key: "fleet", href: "#fleet" },
  { key: "itineraries", href: "#itineraries" },
  { key: "story", href: "#story" },
  { key: "contact", href: "#contact" },
];

export const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (!el) return;
    if (window.lenis) window.lenis.scrollTo(el, { offset: -70, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(href), 350);
    } else {
      scrollTo(href);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else if (window.lenis) window.lenis.scrollTo(0, { duration: 1.2 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 z-50 w-full transition-colors duration-500 ${
        scrolled ? "bg-canvas/90 backdrop-blur-xl border-b border-navy/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 md:px-10 py-4">
        <button
          onClick={goHome}
          data-testid="nav-logo"
          className="flex items-center"
          aria-label="La Rondine Charter"
        >
          <img
            src={scrolled ? LOGO_NAVY : LOGO_LIGHT}
            alt="La Rondine Charter"
            className="h-12 md:h-14 w-auto"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.key}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.key}`}
              className={`text-xs uppercase tracking-[0.18em] font-medium transition-opacity hover:opacity-60 ${
                scrolled ? "text-navy" : "text-canvas"
              }`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div className={`flex items-center text-xs font-medium tracking-wider ${scrolled ? "text-navy" : "text-canvas"}`}>
            <button
              onClick={() => setLang("it")}
              data-testid="lang-it"
              className={lang === "it" ? "opacity-100" : "opacity-40 hover:opacity-70"}
            >
              IT
            </button>
            <span className="mx-1 opacity-30">/</span>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={lang === "en" ? "opacity-100" : "opacity-40 hover:opacity-70"}
            >
              EN
            </button>
          </div>

          <a
            href={waLink(t.waMsg)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-button"
            className={`hidden sm:inline-flex items-center rounded-full border px-5 py-2 text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300 ${
              scrolled
                ? "border-navy text-navy hover:bg-navy hover:text-canvas"
                : "border-canvas text-canvas hover:bg-canvas hover:text-navy"
            }`}
          >
            {t.nav.book}
          </a>

          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-menu-toggle"
            className={`lg:hidden flex flex-col gap-1.5 ${scrolled ? "text-navy" : "text-canvas"}`}
            aria-label="Menu"
          >
            <span className={`block h-px w-6 bg-current transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
            <span className={`block h-px w-6 bg-current transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`block h-px w-6 bg-current transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-canvas border-b border-navy/10"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.key}
                  onClick={() => go(l.href)}
                  data-testid={`mobile-nav-${l.key}`}
                  className="py-3 text-left font-serif text-2xl text-navy border-b border-navy/5 last:border-0"
                >
                  {t.nav[l.key]}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
